import api from '@/services/api';

export type PublicRoomLocale = 'zh-CN' | 'en';

export type PublicRoomStatus = 'draft' | 'published' | 'unpublished';

export interface PublicRoomConfiguration {
  locale: PublicRoomLocale;
  roomId: string;
  status: PublicRoomStatus;
  displayName: string;
  tagline: string;
  description: string;
  highlights: string[];
  sortOrder: number;
  defaultSessionId: string;
  createdBy: string;
  updatedBy: string;
  createdAtUnixMs: number;
  updatedAtUnixMs: number;
  publishedAtUnixMs: number;
  unpublishedAtUnixMs: number;
  sourceAvailable: boolean;
  shareAvailable: boolean;
}

export interface SavePublicRoomConfiguration {
  locale: PublicRoomLocale;
  roomId: string;
  displayName: string;
  tagline: string;
  description: string;
  highlights: string[];
  sortOrder: number;
  defaultSessionId: string;
}

interface PublicRoomReply {
  room: PublicRoomConfiguration;
}
interface ListPublicRoomsReply {
  rooms: PublicRoomConfiguration[];
  nextOffset: number;
}

const base = '/api/v1/admin/public-rooms';
const roomPath = (roomId: string) => `${base}/${encodeURIComponent(roomId)}`;

export const publicRoomsApi = {
  list: (params: {
    locale?: PublicRoomLocale;
    status?: string;
    search?: string;
    pageSize?: number;
    offset?: number;
  }) => api.get<never, ListPublicRoomsReply>(base, { params }),
  get: (roomId: string) => api.get<never, PublicRoomReply>(roomPath(roomId)),
  create: (input: SavePublicRoomConfiguration) =>
    api.post<never, PublicRoomReply>(base, input),
  update: (input: SavePublicRoomConfiguration) =>
    api.patch<never, PublicRoomReply>(roomPath(input.roomId), input),
  remove: (roomId: string, expectedUpdatedAtUnixMs: number) =>
    api.delete<never, Record<string, never>>(roomPath(roomId), {
      data: { expectedUpdatedAtUnixMs },
    }),
  unpublish: (roomId: string) =>
    api.post<never, PublicRoomReply>(`${roomPath(roomId)}/unpublish`, {}),
};

export interface PublicRoomTask {
  taskId: string;
  roomId: string;
  autoPublish: boolean;
  expectedUpdatedAtUnixMs: number;
}
export const publicRoomPreparationApi = {
  request: (
    roomId: string,
    requestId: string,
    publish: boolean,
    version: number,
  ) =>
    api.post<never, Omit<PublicRoomTask, 'expectedUpdatedAtUnixMs'>>(
      `${roomPath(roomId)}/${publish ? 'publication-requests' : 'cache-refreshes'}`,
      { requestId, expectedUpdatedAtUnixMs: version },
    ),
  cancel: (roomId: string, version: number) =>
    api.post<never, PublicRoomReply>(
      `${roomPath(roomId)}/publication-cancellations`,
      {
        expectedUpdatedAtUnixMs: version,
      },
    ),
};
